<template>
  <v-container class="edit-container px-10" fluid>
    <v-popup-loading :isLoading="isLoading" />
    <v-alert-error :show="isError" :close="closeAlert"></v-alert-error>
    <v-alert-success :show="isSuccess" :close="closeAlert"></v-alert-success>
    <alert-confirm
      :show="isCancel"
      title="Discard changes"
      subTitle="Are you sure you want to discard all changes?"
      headline-color="black"
      buttonCancel="OK"
      buttonText="Cancel"
      :close="onCancel"
      :confirm="closeCancelPopup"
      icon=""
    ></alert-confirm>
    <v-row class="mb-4">
      <v-col>
        <v-breadcrumbs
          :items="breadcrumbs"
          divider=">"
          class="px-0"
        ></v-breadcrumbs>
        <v-row class="align-center mr-0" style="margin-left: 1px">
          <h3 class="pageHeadline" t-data="page-headline">
            Edit Appointment Setting
          </h3>
          <v-spacer></v-spacer>
          <edit-buttons
            class="mt-3"
            :show-remove="false"
            :show-preview="false"
            :show-save="!disabledEdit"
            :on-cancel="showCancelPopup"
            :on-save="onSave"
          />
        </v-row>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <v-form ref="form" lazy-validation>
          <v-card>
            <v-card-title class="mt-0 pt-0 pb-0" t-data="card-title">
              <h6 class="mb-0 card-title" t-data="info-title">
                Branch: {{ appointmentSettingObject.name_en }} ({{ appointmentSettingObject.codename.toUpperCase() }})
              </h6>
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text class="mt-0">
              <v-row align="start" justify="start" class="mt-0 px-2 mb-3">
                <v-col cols="12" t-data="info-input">
                  <p class="edit-field-label sub-body-bold" t-data="info-label">
                    Email
                  </p>
                  <v-text-field
                      class="can-disable-selector selector-hide-detail"
                      outlined
                      dense
                      placeholder="Please enter email"
                      v-model="appointmentSettingObject.support_emails"
                      :disabled="disabledEdit"
                  ></v-text-field>
                  <warning-message
                    message="Use commas (,) to separate multiple email addresses. Example: abc@princgroup.com,def@princgroup.com"
                  ></warning-message>
                </v-col>
              </v-row>
              <v-row align="start" justify="start" class="mt-0 px-2 mb-3">
                <v-col cols="12" t-data="info-input">
                  <p class="edit-field-label sub-body-bold" t-data="info-label">
                    Group LineID
                  </p>
                  <v-text-field
                      class="can-disable-selector selector-hide-detail"
                      outlined
                      dense
                      placeholder="Please enter Group LineID"
                      v-model="appointmentSettingObject.support_line_group_id"
                      :disabled="disabledEdit"
                  ></v-text-field>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-form>
        <v-row class="mr-0 mb-3" style="margin-left: 1px; margin-right: 0;">
          <created-detail
            :data-object="appointmentSettingObject"
            :show-create="false"
            update-by-key="last_modified_by"
            update-date-key="last_modified_date"
          />
        </v-row>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import {mapState, mapActions} from 'vuex'
import PopupLoading from '@/components/PopupLoading'
import AlertSuccess from '@/components/AlertSuccess'
import AlertError from '@/components/AlertError'
import AlertConfirm from '@/components/AlertConfirm'
import EditButtons from '@/components/EditButtons'
import WarningMessage from '@/components/WarningMessage'
import CreatedDetail from '@/components/CreatedDetail'
import {
  snackbarSuccessContent,
  snackbarFailContent,
  hasPermissionsByOrgs
} from '@/helper/helper'

export default {
  components: {
    'v-alert-success': AlertSuccess,
    'v-alert-error': AlertError,
    'v-popup-loading': PopupLoading,
    AlertConfirm,
    EditButtons,
    WarningMessage,
    CreatedDetail
  },
  data() {
    return {
      snackTitle: '',
      pageName: 'AppointmentSetting',
      isCreateSuccess: false,
      isCreateFail: false,
      isCancel: false,
      isError: false,
      isSuccess: false,
    }
  },
  computed: {
    snackbarSuccess() {
      return snackbarSuccessContent(this.pageName, this.snackTitle)
    },
    snackbarFail() {
      return snackbarFailContent(this.pageName, this.snackTitle)
    },
    disabledEdit() {
      return !hasPermissionsByOrgs('CanEditAppointmentSetting', [this.$route.params.org_codename])
    },
    breadcrumbs() {
      return [
        {text: 'Dashboard', disabled: false, href: '/'},
        {text: 'Appointment Setting', disabled: false, link: true, href: '/appointment-setting'},
        {text: 'Edit Appointment Setting', disabled: true}
      ]
    },
    ...mapState('appointmentSetting', [
      'appointmentSettingObject',
      'isLoading',
    ])
  },
  async created() {
    await this.fetchDetails()
  },
  methods: {
    async fetchDetails() {
      const orgCodename = this.$route.params.org_codename
      if (orgCodename) {
        await this.getAppointmentSetting(orgCodename)
      }
    },
    showSuccessSnackbar(action) {
      this.isCreateSuccess = true
      this.snackTitle = action
    },
    showCancelPopup() {
      this.isCancel = true
    },
    closeCancelPopup() {
      this.isCancel = false
    },
    onCancel() {
      this.$router.push({
        name: 'appointment-setting-list'
      })
    },
    showFailSnackbar(action) {
      this.isCreateFail = true
      this.snackTitle = action
    },
    async onSave() {
      const status = await this.updateAppointmentSetting({
        org_codename: this.$route.params.org_codename,
        data: {
          support_emails: this.appointmentSettingObject.support_emails ? this.appointmentSettingObject.support_emails.replace(/\s+/g, '').trim() : this.appointmentSettingObject.support_emails,
          support_line_group_id: this.appointmentSettingObject.support_line_group_id ? this.appointmentSettingObject.support_line_group_id.trim() : this.appointmentSettingObject.support_line_group_id
        }
      })
      if (status) {
        this.$router.push({
          name: 'appointment-setting-list',
          query: {isCreateSuccess: true, snackTitle: 'edited'}
        })
      } else {
        return (this.isError = true)
      }
    },
    closeAlert() {
      this.isError = false
      this.isSuccess = false
    },
    ...mapActions('appointmentSetting', [
      'getAppointmentSetting',
      'updateAppointmentSetting',
      'resetState'
    ])
  },
  beforeRouteLeave(to, from, next) {
    this.resetState()
    next()
  }
}
</script>

<style>
.selector-hide-detail > .v-input__control > .v-text-field__details {
  display: none;
}

</style>
